import { useMemo, useState } from "react";
import { Loader, Center } from "@mantine/core";
import { IconFileInvoice, IconFileOff } from "@tabler/icons-react";
import {
  SupplierInvoice,
  useSupplierInvoicesQuery,
} from "../../api/queries";
import {
  YearFolder,
  countItemsInYear,
  groupByYearAndMonth,
  sortedYears as sortYearsDesc,
} from "../archiveTree";
import { SupplierInvoiceItem } from "./SupplierInvoiceItem";
import { SupplierInvoiceFilters } from "./SupplierInvoiceFilters";

const INVOICE_NOUN = { singular: "invoice", plural: "invoices" };

const getInvoiceDate = (invoice: SupplierInvoice) => invoice.date_sent;

const getInvoiceKey = (invoice: SupplierInvoice) =>
  `${invoice.gmail_message_id}:${invoice.attachment_id}`;

export const SupplierInvoices = () => {
  const { data, isLoading, error } = useSupplierInvoicesQuery();
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const [supplier, setSupplier] = useState("");

  const invoices = data?.invoices;
  const supplierOptions = data?.suppliers ?? [];

  const filteredInvoices = useMemo(() => {
    if (!invoices) return [];

    return invoices.filter((invoice) => {
      if (supplier && invoice.supplier_name !== supplier) {
        return false;
      }

      if (search.trim()) {
        const needle = search.toLowerCase().trim();
        const matches =
          invoice.subject.toLowerCase().includes(needle) ||
          invoice.filename.toLowerCase().includes(needle);
        if (!matches) return false;
      }

      return true;
    });
  }, [invoices, search, supplier]);

  const groupedInvoices = useMemo(() => {
    if (!filteredInvoices.length) return {};
    return groupByYearAndMonth(filteredInvoices, getInvoiceDate);
  }, [filteredInvoices]);

  const sortedYears = useMemo(
    () => sortYearsDesc(groupedInvoices),
    [groupedInvoices],
  );

  const toggleYear = (year: number) => {
    setExpandedYears((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(year)) {
        newSet.delete(year);
      } else {
        newSet.add(year);
      }
      return newSet;
    });
  };

  const handleClearFilters = () => {
    setSearch("");
    setSupplier("");
  };

  if (isLoading) {
    return (
      <Center className="h-full pb-24">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center className="h-full pb-24">
        <p className="text-red-600">Failed to load supplier invoices.</p>
      </Center>
    );
  }

  const hasActiveFilters = Boolean(search || supplier);

  return (
    <div className="flex flex-col gap-8 w-full py-8 px-4">
      <div className="flex flex-col gap-6 bg-linear-to-br from-slate-50 to-slate-100 rounded-xl shadow-lg border border-slate-200 p-6 w-full">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl bg-linear-to-br from-slate-600 to-slate-700 text-white shadow-md">
            <IconFileInvoice size={28} stroke={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Supplier Invoices
            </h1>
            <p className="text-sm text-gray-600">
              Invoices received by email, organized by month
            </p>
          </div>
        </div>

        <SupplierInvoiceFilters
          search={search}
          supplier={supplier}
          supplierOptions={supplierOptions}
          onSearchChange={setSearch}
          onSupplierChange={setSupplier}
          onClearFilters={handleClearFilters}
        />

        {sortedYears.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-gray-200">
            <IconFileOff size={48} className="text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">No supplier invoices found</p>
            <p className="text-gray-400 text-sm mt-1">
              {hasActiveFilters
                ? "No invoices match your filters. Try adjusting your search criteria."
                : "Invoices will appear here once suppliers email them to a connected mailbox"}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            {sortedYears.map((year) => (
              <YearFolder
                key={year}
                year={year}
                monthData={groupedInvoices[year]}
                itemCount={countItemsInYear(groupedInvoices, year)}
                isExpanded={expandedYears.has(year)}
                onToggle={() => toggleYear(year)}
                getDate={getInvoiceDate}
                getItemKey={getInvoiceKey}
                renderItem={(invoice) => (
                  <SupplierInvoiceItem invoice={invoice} />
                )}
                itemNoun={INVOICE_NOUN}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierInvoices;
